import { Grid, Tabs, Typography } from '@mui/material';
import {
    TransactionStatusCode,
    TransactionStatusCodeMap
} from '@tassat/common-definitions';
import { Asset, CollateralHistoryDetailRecord } from '@tassat/fred-definitions';
import { TabPanel } from '@tassat/layout';
import { StyledTab } from '@tassat/menu';
import { TransactionDetail } from '@tassat/text';
import { formatAsAsset } from '@tassat/utility';
import React from 'react';

import { PledgeTransactionsTable } from './pledgeTransactionsTable/pledgeTransactionsTable';
import { muiStyles } from './styles';
import { PledgeHeaderSectionDetailsProps, PledgeSectionDetailsProps } from './types';
import { FromFirmDetail, ToFirmDetail } from '../pledgeFirmDetails/pledgeFirmDetails';

const PledgeSectionDetails = (props: PledgeSectionDetailsProps) => {
    const {
        selectedPledge,
        currentUser,
        declineReason
    } = props;

    const { PledgeParticipant } = selectedPledge;

    const classes = muiStyles();

    return (
        <Grid
            className={classes.detailSectionContainer}
            container
            direction='row'
            item
            justifyContent='space-around'
            spacing={0}
            wrap='wrap'
            xs={12}
        >
            <FromFirmDetail
                currentUser={currentUser}
                selectedPledge={selectedPledge}
            />
            <ToFirmDetail
                currentUser={currentUser}
                selectedPledgeParticipants={PledgeParticipant}
            />
            <Grid
                className={classes.detailSection}
                container
                direction='column'
                item
                spacing={0}
                wrap='nowrap'
            >
                <TransactionDetail
                    attribute={formatAsAsset(selectedPledge.Price)}
                    id='pledgeDetails_price'
                    label='Price'
                    orientation='column'
                />
                <TransactionDetail
                    attribute={selectedPledge.ApiTransactionReferenceNumber || '-'}
                    id='pledgeDetails_transactionApiReference'
                    label='API Tran Ref'
                    orientation='column'
                />
                <TransactionDetail
                    attribute={selectedPledge.APIConnectionName || '-'}
                    id='pledgeDetails_transactionApiName'
                    label='API Name'
                    orientation='column'
                />
                <TransactionDetail
                    attribute={selectedPledge.PublicDescription || '-'}
                    id='pledgeDetails_publicDescription'
                    label='Public Description'
                    orientation='column'
                />
                <TransactionDetail
                    attribute={selectedPledge.PrivateDescription || '-'}
                    id='pledgeDetails_privateDescription'
                    label='Private Description'
                    orientation='column'
                />
                {declineReason && (
                    <TransactionDetail
                        attribute={declineReason}
                        id='pledgeDetails_declineReason'
                        label='Decline Reason'
                        orientation='column'
                    />
                )}
            </Grid>
        </Grid>
    );
};

export const PledgeHeaderSectionDetails = (props: PledgeHeaderSectionDetailsProps) => {
    const {
        isSecurityAssetTxn,
        selectedPledge,
        currentRole,
        currentUser,
        declineReason,
        asset
    } = props;

    const { PledgeTransaction } = selectedPledge;

    const classes = muiStyles();
    const [ currentTab, setCurrentTab ] = React.useState<number>(0);
    const hidePendingWord = (
        selectedPledge.Status === TransactionStatusCode.DECLINED ||
        selectedPledge.Status === TransactionStatusCode.FAILED ||
        selectedPledge.Status === TransactionStatusCode.REJECTED
    );

    const renderAmount = (selectedPledge: CollateralHistoryDetailRecord, asset: Asset, showSecurityAmount: boolean) => {
        if (showSecurityAmount) {
            return selectedPledge.AssetAmount ?
                formatAsAsset(selectedPledge.AssetAmount, asset, 'ticker', true, true)
                : `${asset.Ticker}: ${hidePendingWord ? '-' : '0'}`;
        }
    };

    const SectionDetails = React.useMemo(() => (
        <>
            <Grid
                className={classes.headerAmountDetailsMain}
                container
                direction='row'
                item
                spacing={0}
                wrap='wrap'
                xs={12}
            >
                <Grid
                    item
                    xs={5}
                >
                    <Typography
                        className={classes.transactionAmount}
                        id='pledgeDetails_transactionAmount_value'
                        variant='h3'
                    >
                        <strong>
                            {renderAmount(selectedPledge, asset, isSecurityAssetTxn)}
                        </strong>
                    </Typography>
                </Grid>
                <Grid
                    className={classes.headerDetailsContainer}
                    container
                    direction='column'
                    item
                    spacing={0}
                    xs={7}
                >
                    <Grid
                        container
                        direction='row'
                        item
                        spacing={0}
                        wrap='wrap'
                    >
                        <Grid
                            className={classes.headerSmallDetail}
                            container
                            direction='row'
                            item
                            spacing={0}
                            wrap='nowrap'
                        >
                            <Typography
                                className={classes.headerLabel}
                                id='pledgeDetails_transactionType_label'
                                variant='body2'
                            >
                                Type:
                            </Typography>
                            <Typography
                                className={classes.headerAttribute}
                                id='pledgeDetails_transactionType_value'
                                variant='body1'
                            >
                                {selectedPledge.TransactionType === 'Pledge' ? 'Post' : selectedPledge.TransactionType}
                            </Typography>
                        </Grid>
                        <Grid
                            className={classes.headerSmallDetail}
                            container
                            direction='row'
                            item
                            spacing={0}
                            wrap='nowrap'
                        >
                            <Typography
                                className={classes.headerLabel}
                                id='pledgeDetails_transactionStatus_label'
                                variant='body2'
                            >
                                Status:
                            </Typography>
                            <Typography
                                className={classes.headerAttribute}
                                id='pledgeDetails_transactionStatus_value'
                                variant='body1'
                            >
                                {TransactionStatusCodeMap[selectedPledge.Status]?.displayValue ?? ''}
                            </Typography>
                        </Grid>
                    </Grid>
                    <Grid
                        className={classes.headerSmallDetail}
                        container
                        direction='row'
                        item
                        spacing={0}
                        wrap='nowrap'
                    >
                        <Typography
                            className={classes.headerLabel}
                            id='pledgeDetails_pledgeId_label'
                            variant='body2'
                        >
                            Post ID:
                        </Typography>
                        <Typography
                            className={`${classes.headerAttribute} ${classes.headerAttributeNoCaps}`}
                            id='pledgeDetails_confirmationId_value'
                            variant='body1'
                        >
                            {selectedPledge.PledgeId}
                        </Typography>
                    </Grid>
                </Grid>
            </Grid>
            <PledgeSectionDetails
                currentRole={currentRole}
                currentUser={currentUser}
                declineReason={declineReason}
                selectedPledge={selectedPledge}
            />
        </>
    ), [selectedPledge]);

    return (
        <Grid
            container
            item
            justifyContent='center'
            xs={12}
        >
            <Grid
                className={classes.mainTabContainer}
                item
                xs={9}
            >
                <Tabs
                    centered
                    classes={{ indicator: classes.indicator }}
                    className={classes.userTabsContainer}
                    onChange={(e, newTab) => setCurrentTab(newTab)}
                    value={currentTab}
                >
                    <StyledTab
                        id='pledgeDetailsTab'
                        label='Details'
                        tabClasses={{
                            root: {
                                fontSize: '0.815rem',
                                minHeight: '0rem',
                                height: '100%',
                                width: '100%',
                                backgroundColor: '#fff',
                                opacity: '1'
                            }
                        }}
                    />
                    <StyledTab
                        id='pledgeActivityTab'
                        label='Activity'
                        tabClasses={{
                            root: {
                                fontSize: '0.815rem',
                                minHeight: '0rem',
                                height: '100%',
                                width: '100%',
                                backgroundColor: '#fff',
                                opacity: '1'
                            }
                        }}
                    />
                </Tabs>
            </Grid>
            <TabPanel
                index={0}
                value={currentTab}
            >
                {SectionDetails}
            </TabPanel>
            <TabPanel
                index={1}
                value={currentTab}
            >
                <Grid
                    container
                    item
                    justifyContent='center'
                    xs={12}
                >
                    <PledgeTransactionsTable
                        asset={asset}
                        currentRole={currentRole}
                        currentUser={currentUser}
                        pledgeTransactionList={PledgeTransaction}
                    />
                </Grid>
            </TabPanel>
        </Grid>
    );
};